import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CircleNotch, UploadSimple } from "@phosphor-icons/react";

const PAYMENT_METHODS = ["Bank Transfer", "PayPal", "Stripe", "UPI", "Cash", "Other"];
const CURRENCIES = ["EUR", "USD", "GBP", "INR"];

export default function CustomerNewOrder() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company: "", country: "Spain", address: "",
    product: "", quantity: 1, weight: 0,
    total_amount: 0, currency: "EUR",
    payment_method: "Bank Transfer", payment_reference: "", remarks: "",
  });
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);

  const s = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity, 10) || 1,
        weight: parseFloat(form.weight) || 0,
        total_amount: parseFloat(form.total_amount) || 0,
      };
      const { data: order } = await api.post("/customer/orders", payload);

      if (proof) {
        const fd = new FormData();
        fd.append("file", proof);
        try {
          await api.post(`/customer/orders/${order.order_id}/payment-proof`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.error("Order created, but payment proof upload failed. You can upload it later.");
        }
      }

      toast.success(`Order #${order.order_number} placed. A confirmation email is on the way.`);
      navigate(`/customer/orders/${order.order_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="customer-new-order-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">New Order</p>
        <h1 className="text-3xl font-black tracking-tighter">Place an order</h1>
        <p className="text-sm text-zinc-500 mt-1">Fill in the details below. You&apos;ll receive an email confirmation once submitted.</p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="customer-new-order-form">
        <Card title="Delivery">
          <div className="grid grid-cols-2 gap-3">
            <F label="Company (optional)"><Input value={form.company} onChange={s("company")} data-testid="new-company" /></F>
            <F label="Country"><Input required value={form.country} onChange={s("country")} data-testid="new-country" /></F>
          </div>
          <F label="Full delivery address">
            <Textarea required rows={2} value={form.address} onChange={s("address")} placeholder="Street, city, postcode…" data-testid="new-address" />
          </F>
        </Card>

        <Card title="Order">
          <div className="grid grid-cols-4 gap-3">
            <F label="Product"><Input required value={form.product} onChange={s("product")} data-testid="new-product" /></F>
            <F label="Quantity"><Input type="number" min="1" required value={form.quantity} onChange={s("quantity")} data-testid="new-quantity" /></F>
            <F label="Weight (kg)"><Input type="number" step="0.01" value={form.weight} onChange={s("weight")} data-testid="new-weight" /></F>
            <F label="Total Amount">
              <div className="flex gap-2">
                <Input type="number" step="0.01" required value={form.total_amount} onChange={s("total_amount")} data-testid="new-total" />
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="w-24" data-testid="new-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </F>
          </div>
        </Card>

        <Card title="Payment">
          <div className="grid grid-cols-2 gap-3">
            <F label="Payment method">
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="new-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Payment reference (optional)">
              <Input value={form.payment_reference} onChange={s("payment_reference")} placeholder="Transaction ID / bank ref" data-testid="new-payment-ref" />
            </F>
          </div>
          <F label="Payment proof (optional — image or PDF, max 10 MB)">
            <Input type="file" accept="image/*,application/pdf" onChange={(e) => setProof(e.target.files[0])} data-testid="new-payment-proof" />
            {proof && <p className="mt-1 text-xs text-zinc-500">Selected: {proof.name}</p>}
          </F>
          <F label="Remarks (optional)">
            <Textarea rows={2} value={form.remarks} onChange={s("remarks")} placeholder="Anything we should know…" data-testid="new-remarks" />
          </F>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/customer")} className="border-zinc-300">Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="submit-order">
            {busy ? <><CircleNotch className="animate-spin mr-1" size={14} /> Submitting…</> : <><UploadSimple size={14} weight="bold" className="mr-1" /> Submit order</>}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-zinc-200 bg-white p-6">
      <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-4">{title}</p>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
